// src/components/ProtectedRoute.js
import React, { useEffect, useState, useRef } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { Spin } from "antd";
import { getRolByNombre } from "../../services/rolesService";
import useAuth from "../../hooks/useAuth";

/** Normaliza permisos API a booleanos de lectura (SOLO read: true) */
function normalizeReadPerms(permisosRaw) {
  const out = {};
  if (!permisosRaw || typeof permisosRaw !== "object") return out;

  for (const key of Object.keys(permisosRaw)) {
    const v = permisosRaw[key];
    if (v && typeof v === "object" && "read" in v) {
      out[key] = Boolean(v.read);
    } else if (typeof v === "boolean") {
      out[key] = v;
    } else {
      out[key] = false;
    }
  }
  return out;
}

/** Mapeo de rutas a módulos de permisos */
const ROUTE_TO_MODULE = {
  "/futbolito": "dashboard",
  "/futbolito/dashboard": "dashboard",
  "/futbolito/colaboradores": "colaboradores",
  "/futbolito/representantes": "representantes",
  "/futbolito/deportistas": "deportistas",
  "/futbolito/grupos": "grupos",
  "/futbolito/eventos": "eventos",
  "/futbolito/asistencias": "asistencias",
  "/futbolito/notificaciones": "notificaciones",
  "/futbolito/roles": "roles",
  "/futbolito/pagos": "pagos",
};

const ProtectedRoute = ({ children, requiredModule = null }) => {
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [readPerms, setReadPerms] = useState(null);
  const [loadingPerms, setLoadingPerms] = useState(false);
  const [permError, setPermError] = useState(null);
  const lastLoadedRole = useRef("");

  const rolRaw = user?.user_metadata?.rol || user?.role || "";
  const rolNorm = (rolRaw ?? "").toString();

  // Determinar qué módulo se requiere para la ruta actual
  const currentModule = requiredModule || ROUTE_TO_MODULE[location.pathname];

  /** Cargar permisos desde API */
  useEffect(() => {
    let disposed = false;

    async function load() {
      const role = rolNorm;
      if (!role || !isAuthenticated) {
        setReadPerms({});
        return;
      }

      // Evitar recarga si ya tenemos permisos
      if (lastLoadedRole.current === role && readPerms !== null) return;

      setLoadingPerms(true);
      setPermError(null);

      try {
        const data = await getRolByNombre(role);
        if (!disposed) {
          const normalized = normalizeReadPerms(data?.permisos);
          setReadPerms(normalized);
          lastLoadedRole.current = role;
        }
      } catch (e) {
        if (!disposed) {
          setPermError(e?.message || "No se pudieron obtener permisos");
          setReadPerms({});
        }
      } finally {
        if (!disposed) setLoadingPerms(false);
      }
    }

    load();

    return () => {
      disposed = true;
    };
  }, [rolNorm, isAuthenticated]);

  const handleGoBack = () => {
    navigate(-1);
  };

  // Mostrar loading mientras carga autenticación o permisos
  if (authLoading || loadingPerms) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="fixed inset-0 flex items-center justify-center bg-white bg-opacity-80 z-50">
          <Spin size="large" />
        </div>
      </div>
    );
  }

  // Verificar autenticación
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Si hay error cargando permisos
  if (permError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">
            Error al cargar permisos
          </h2>
          <p className="text-gray-600 mb-4">{permError}</p>
          <button
            onClick={handleGoBack}
            className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition duration-300 shadow-sm hover:shadow-md"
          >
            Regresar
          </button>
        </div>
      </div>
    );
  }

  // Verificar permisos de lectura para el módulo actual
  if (currentModule && readPerms) {
    const hasReadPermission = readPerms[currentModule] === true;

    if (!hasReadPermission) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">
              Acceso Denegado
            </h2>
            <p className="text-gray-600 mb-2">
              No tienes permisos de lectura para acceder a este módulo.
            </p>
            <p className="text-sm text-gray-500 mb-4">
              Módulo: <span className="font-semibold">{currentModule}</span>
            </p>
            <button
              onClick={handleGoBack}
              className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition duration-300 shadow-sm hover:shadow-md"
            >
              Regresar
            </button>
          </div>
        </div>
      );
    }
  }

  return children;
};

export default ProtectedRoute;